import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMantineColorScheme } from "@mantine/core";
import * as atlas from "azure-maps-control";
import "azure-maps-control/dist/atlas.min.css";
import "./projectMapModal.scss";
import { useDynamicTableHeight } from "../../hooks/useDynamicTableHeight";

// const mapKey = "nkw0wk8J4yUOJeQoQsBL42iT6q0Hx_VnV0I0JKX1diw";
const mapKey = "CUbVpzDGU704H09q02oHaSWB3hc1uUeahJ1PWlUex7LkkDII9rCCJQQJ99CFACYeBjFCtWGOAAAgAZMP4EQY";


const ProjectMapView = ({ projects = [], height = "75vh", onProjectOpen, resetCounter = 0 }) => {
  const { colorScheme } = useMantineColorScheme();
  const [searchTerm, setSearchTerm] = useState("");
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const dataSourceRef = useRef(null);
  const popupRef = useRef(null);
  const popupModeRef = useRef(null); // 'hover' | 'click' | null
  const hoverCloseTimerRef = useRef(null);
  const isHoveringPopupRef = useRef(false);
  const initialCameraRef = useRef(null);
  const suppressNextDocCloseRef = useRef(false);
  const documentPointerHandlerRef = useRef(null);
  const controlsInitializedRef = useRef(false);
  const [mapInitialized, setMapInitialized] = useState(false);

  const containerId = useRef(`project-map-container-${Date.now()}`);

  const validProjects = useMemo(
    () =>
      (projects || []).filter(
        (p) =>
          p.locationDTO?.latitude != null &&
          p.locationDTO?.longitude != null &&
          !isNaN(parseFloat(p.locationDTO.latitude)) &&
          !isNaN(parseFloat(p.locationDTO.longitude))
      ),
    [projects]
  );

  // Use the dynamic table-height hook with the same settings as the table
  const { getTableHeight } = useDynamicTableHeight({
    rowHeight: 40,
    headerHeight: 48,
    footerHeight: 48,
    reservedSpace: 170,
    minRows: 5,
  });

  const computedHeight = useMemo(() => {
    // If parent passed a percentage (e.g. "100%"), we want the map to match the
    // datatable height (returned by `getTableHeight()` which is in px). If a fixed
    // height string (like '75vh' or '500px') is provided, prefer it.
    if (typeof height === "string" && height.trim().endsWith("%")) {
      return getTableHeight();
    }
    if (typeof height === "string") return height;
    return getTableHeight();
  }, [height, getTableHeight]);

  const getInheritedCssZoom = (el) => {
    if (typeof window === "undefined" || !el) return 1;
    let node = el;

    while (node && node !== document.documentElement) {
      try {
        const z = parseFloat(window.getComputedStyle(node).zoom);
        if (Number.isFinite(z) && z > 0 && Math.abs(z - 1) > 0.001) {
          return z;
        }
      } catch (e) {
        // ignore and continue walking up
      }
      node = node.parentElement;
    }

    return 1;
  };

  const applyMapZoomCompensation = () => {
    const mapEl = mapContainerRef.current;
    if (!mapEl) return;

    const inheritedZoom = getInheritedCssZoom(mapEl.parentElement || mapEl);
    const compensation = inheritedZoom > 0 ? 1 / inheritedZoom : 1;

    // Neutralize ancestor CSS zoom for map hit-testing (hover/click accuracy).
    mapEl.style.zoom = `${compensation * 100}%`;
    mapEl.style.transformOrigin = "top left";
  };

  const addMarkers = (dataSource, map) => {
    if (!dataSource || !map) return;
    dataSource.clear();
    // Group projects that share (approximately) the same coordinates so a single
    // marker represents all projects at that location and shows a list on open.
    const points = validProjects.map((project) => {
      const lat = parseFloat(project.locationDTO.latitude);
      const lon = parseFloat(project.locationDTO.longitude);

      const designId = project.designWithVersionsDTOList?.[0]?.designDTO?.designId;
      const designVersionId =
        project.designWithVersionsDTOList?.[0]?.designVersionDTO?.[0]?.designVersionId;

      return new atlas.data.Feature(new atlas.data.Point([lon, lat]), {
        projectName: project.projectName || "Unknown Project",
        customerName: project.customerName || "",
        city: project.locationDTO?.city || "",
        country: project.locationDTO?.country || "",
        projectId: project.projectId,
        designId,
        designVersionId,
      });
    });

    dataSource.add(points);

    if (points.length > 0) {
      if (points.length === 1) {
        const singleCoords = points[0].geometry.coordinates;
        map.setCamera({ center: singleCoords, zoom: 6, bearing: 0, pitch: 0 });
      } else {
        const bbox = atlas.data.BoundingBox.fromData(points);
        map.setCamera({ bounds: bbox, padding: 50, bearing: 0, pitch: 0 });
      }

      // Capture the fitted view so we can reset to it later.
      // `setCamera` is applied asynchronously; defer `getCamera` slightly.
      setTimeout(() => {
        try {
          initialCameraRef.current = map.getCamera();
        } catch (e) {
          // no-op
        }
      }, 0);
    }
  };

  const handleSearch = () => {
    if (!searchTerm || !mapRef.current) return;
    const term = searchTerm.trim().toLowerCase();
    const match = validProjects.find((p) => {
      const pn = (p.projectName || '').toLowerCase();
      const cn = (p.customerName || '').toLowerCase();
      return pn.includes(term) || cn.includes(term);
    });
    if (match && mapRef.current && typeof mapRef.current.openProject === 'function') {
      mapRef.current.openProject(match);
    } else if (match && mapRef.current) {
      const lat = parseFloat(match.locationDTO.latitude);
      const lon = parseFloat(match.locationDTO.longitude);
      if (isFinite(lat) && isFinite(lon)) {
        mapRef.current.setCamera({ center: [lon, lat], zoom: 12 });
      }
    } else {
      try { window.alert('No matching project found'); } catch (e) {}
    }
  };

  const resetMapView = () => {
    const map = mapRef.current;
    if (!map) return;

    const validCoords = (validProjects || [])
      .map((p) => [parseFloat(p.locationDTO.longitude), parseFloat(p.locationDTO.latitude)])
      .filter(([lon, lat]) => isFinite(lon) && isFinite(lat));

    if (validCoords.length === 1) {
      map.setCamera({ center: validCoords[0], zoom: 6, bearing: 0, pitch: 0 });
      return;
    }

    if (validCoords.length > 1) {
      const lonVals = validCoords.map((c) => c[0]);
      const latVals = validCoords.map((c) => c[1]);
      const bounds = [
        Math.min(...lonVals),
        Math.min(...latVals),
        Math.max(...lonVals),
        Math.max(...latVals),
      ];
      map.setCamera({ bounds, padding: 50, bearing: 0, pitch: 0 });
      return;
    }

    const cam = initialCameraRef.current;
    if (cam?.center && typeof cam.zoom === "number") {
      map.setCamera({ center: cam.center, zoom: cam.zoom, bearing: 0, pitch: 0 });
    }
  };

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;
    // if (!validProjects.length) return;

    applyMapZoomCompensation();

    const map = new atlas.Map(mapContainerRef.current, {
      authOptions: {
        authType: "subscriptionKey",
        subscriptionKey: mapKey,
      },
      style: colorScheme === "dark" ? "night" : "light",
      center: [0, 0],
      zoom: 1,
      view: "Auto",
      // Keep map tiles repeating horizontally when zoomed far out.
      renderWorldCopies: true,
    });

    // Set ref immediately so the guard prevents a second map being created
    // while this one is still initialising (React Strict Mode double-invoke).
    mapRef.current = map;
    let disposed = false;

    map.events.add("ready", () => {
      if (disposed) return;
      setMapInitialized(true);

      // Add controls once. Keep reset as a React overlay button to avoid custom-control race issues.
      if (!controlsInitializedRef.current) {
        try { map.controls.add(new atlas.control.ZoomControl(), { position: "top-right" }); } catch (e) {}
        try { map.controls.add(new atlas.control.StyleControl(), { position: "top-right" }); } catch (e) {}
        try { map.controls.add(new atlas.control.FullscreenControl({ style: "auto" }), { position: "top-right" }); } catch (e) {}
        controlsInitializedRef.current = true;
      }

      const shortcuts = document.getElementById("atlas-map-shortcuts");
      const state = document.getElementById("atlas-map-state");
      if (shortcuts) shortcuts.style.visibility = "hidden";
      if (state) state.style.visibility = "hidden";

      const dataSource = new atlas.source.DataSource(null, {
        cluster: true,
        // Pixel radius used to group nearby points. Tune for your UX.
        clusterRadius: 45,
        // Keep clustering active up to high zoom so points with identical
        // coordinates remain grouped instead of splitting into single markers.
        clusterMaxZoom: 22,
      });
      map.sources.add(dataSource);
      dataSourceRef.current = dataSource;

      const popup = new atlas.Popup({ closeButton: false, pixelOffset: [0, -30] });
      popupRef.current = popup;
      popupModeRef.current = null;
      isHoveringPopupRef.current = false;

      // Remove the browser tooltip/title from the popup close button ("Close")
      const removePopupCloseTitle = () => {
        setTimeout(() => {
          try {
            const container = document.getElementById(containerId.current);
            const nodes = container
              ? container.querySelectorAll('.atlas-popup .atlas-popup-close')
              : document.querySelectorAll('.atlas-popup .atlas-popup-close');
            nodes.forEach((btn) => {
              try {
                if (btn && btn.removeAttribute) btn.removeAttribute('title');
                if (btn && btn.setAttribute) btn.setAttribute('aria-label', 'Close');
              } catch (e) {
                // ignore
              }
            });
          } catch (e) {
            // ignore
          }
        }, 0);
      };

      const openAndSanitize = (p) => {
        if (!p) return;
        p.open(map);
        removePopupCloseTitle();
      };

      // expose helper to zoom/open a project by object or id
      map.openProject = (project) => {
        try {
          const proj = typeof project === 'string' || typeof project === 'number'
            ? validProjects.find(p => String(p.projectId) === String(project))
            : project;
          if (!proj) return;
          const lat = parseFloat(proj.locationDTO.latitude);
          const lon = parseFloat(proj.locationDTO.longitude);
          if (!isFinite(lat) || !isFinite(lon)) return;
          const coordinates = [lon, lat];
          map.setCamera({ center: coordinates, zoom: 12 });
          const properties = {
            projectName: proj.projectName || 'Unknown Project',
            customerName: proj.customerName || '',
            city: proj.locationDTO?.city || '',
            country: proj.locationDTO?.country || '',
            projectId: proj.projectId,
          };
          popup.setOptions({ closeButton: true, content: buildPopupContent(properties, { variant: 'click' }), position: coordinates, pixelOffset: [0, -30] });
          popup.open(map);
        } catch (e) {}
      };

      const getShapeCoordinates = (shape) => {
        if (!shape) return null;
        if (typeof shape.getCoordinates === "function") return shape.getCoordinates();
        if (shape.geometry && Array.isArray(shape.geometry.coordinates)) return shape.geometry.coordinates;
        if (Array.isArray(shape.coordinates)) return shape.coordinates;
        return null;
      };

      const getShapeProperties = (shape) => {
        if (!shape) return {};
        if (typeof shape.getProperties === "function") return shape.getProperties();
        return shape.properties || shape || {};
      };

      // Cluster bubbles (shows count when many points overlap)
            // Helper: get all features in a cluster by cluster_id
            const getClusterLeaves = (clusterId, limit = 50) => {
              return new Promise((resolve, reject) => {
                dataSource.getClusterLeaves(clusterId, limit, 0)
                  .then(resolve)
                  .catch(reject);
              });
            };

            // Helper: build a DOM popup for a cluster with a list of projects
            const buildClusterPopupContent = (features, coordinates) => {
              const root = document.createElement("div");
              root.style.padding = "10px";
              root.style.fontFamily = "sans-serif";
              root.style.background = colorScheme === "dark" ? "#1f2729" : "white";
              root.style.borderRadius = "4px";
              root.style.boxShadow = "0 2px 8px rgba(0,0,0,0.20)";
              root.style.maxWidth = "260px";
              root.style.maxHeight = "180px";
              root.style.overflowY = "auto";

              const title = document.createElement("div");
              title.textContent = `Projects (${features.length})`;
              title.style.fontWeight = "700";
              title.style.fontSize = "14px";
              title.style.marginBottom = "6px";
              root.appendChild(title);

              // Keep hover tooltip open while interacting with the popup content.
              root.addEventListener("mouseenter", () => {
                isHoveringPopupRef.current = true;
                if (hoverCloseTimerRef.current) {
                  clearTimeout(hoverCloseTimerRef.current);
                  hoverCloseTimerRef.current = null;
                }
              });
              root.addEventListener("mouseleave", () => {
                isHoveringPopupRef.current = false;
                if (popupModeRef.current === "cluster-hover") {
                  if (popupRef.current) popupRef.current.close();
                  popupModeRef.current = null;
                }
              });

              // Flatten features: features may represent grouped points (with a `projects` array)
              const items = [];
              features.forEach((f) => {
                const p = typeof f.getProperties === "function" ? f.getProperties() : (f.properties || f || {});
                if (Array.isArray(p.projects) && p.projects.length > 0) {
                  p.projects.forEach((sub) => items.push(sub));
                } else {
                  items.push(p);
                }
              });

              const getVal = (obj, keys) => {
                for (let k of keys) {
                  if (obj && Object.prototype.hasOwnProperty.call(obj, k) && obj[k] !== undefined) return obj[k];
                }
                return undefined;
              };

              items.forEach((p) => {
                const displayName = getVal(p, ["projectName", "project_name", "name"]) || getVal(p, ["id"]) || "Unknown Project";
                const projId = getVal(p, ["projectId", "project_id", "id"]);
                const designId = getVal(p, ["designId", "design_id"]);
                const designVersionId = getVal(p, ["designVersionId", "design_version_id"]);

                const item = document.createElement("div");
                item.textContent = displayName;
                item.style.fontSize = "13px";
                item.style.color = "#0087CD";
                item.style.cursor = "pointer";
                item.style.padding = "4px 0";
                item.style.textDecoration = "underline";
                item.setAttribute("role", "button");
                item.setAttribute("tabindex", "0");
                item.addEventListener("click", (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (typeof onProjectOpen === "function") {
                    onProjectOpen(projId, designId, designVersionId);
                  }
                  if (popupRef.current) popupRef.current.close();
                  popupModeRef.current = null;
                });
                item.addEventListener("keydown", (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    if (typeof onProjectOpen === "function") {
                      onProjectOpen(projId, designId, designVersionId);
                    }
                    if (popupRef.current) popupRef.current.close();
                    popupModeRef.current = null;
                  }
                });
                root.appendChild(item);
              });
              return root;
            };
            // Show hover popup with project list on cluster hover
            // (attached after cluster layer is created below)
      const clusterBubbleLayer = new atlas.layer.BubbleLayer(dataSource, null, {
        filter: ["has", "point_count"],
        radius: [
          "step",
          ["get", "point_count"],
          14,
          10,
          16,
          25,
          20,
          50,
          24,
          100,
          28,
        ],
        color: "#0087CD",
        strokeColor: "#ffffff",
        strokeWidth: 2,
      });
      map.layers.add(clusterBubbleLayer);

        // Attach cluster hover handlers after cluster layer is available
        map.events.add("mouseenter", clusterBubbleLayer, (e) => {
          console.debug("clusterBubbleLayer mouseenter", { shapes: e?.shapes });
          map.getCanvasContainer().style.cursor = "pointer";
          if (!e.shapes || e.shapes.length === 0) return;
          const shape = e.shapes[0];
          const props = getShapeProperties(shape);
          const coordinates = getShapeCoordinates(shape);
          if (!coordinates) return;
          const clusterId = props.cluster_id ?? props.clusterId;
          if (clusterId == null) return;

          // Only show if not already open for this cluster
          if (popupModeRef.current === "cluster-hover" && popupRef.current?.getOptions()?.position?.toString() === coordinates.toString()) {
            return;
          }

          getClusterLeaves(clusterId, 50)
            .then((features) => {
              console.debug("getClusterLeaves success", { clusterId, count: features?.length });
              if (!features || features.length === 0) return;
              if (!popupRef.current) return;
              popupModeRef.current = "cluster-hover";
              popupRef.current.setOptions({
                closeButton: false,
                content: buildClusterPopupContent(features, coordinates),
                position: coordinates,
                pixelOffset: [0, 0],
              });
              openAndSanitize(popupRef.current);
            })
            .catch((err) => {
              console.error("getClusterLeaves failed", err);
              if (!popupRef.current) return;
              popupModeRef.current = "cluster-hover";
              const fallback = document.createElement("div");
              fallback.textContent = "Projects available — click cluster to view";
              fallback.style.padding = "8px";
              popupRef.current.setOptions({ closeButton: false, content: fallback, position: coordinates, pixelOffset: [0, 0] });
              openAndSanitize(popupRef.current);
            });
        });

        // Hide popup when leaving cluster
        map.events.add("mouseleave", clusterBubbleLayer, () => {
          map.getCanvasContainer().style.cursor = "grab";
          if (popupModeRef.current === "cluster-hover" && popupRef.current) {
            if (hoverCloseTimerRef.current) {
              clearTimeout(hoverCloseTimerRef.current);
              hoverCloseTimerRef.current = null;
            }
            hoverCloseTimerRef.current = setTimeout(() => {
              if (!isHoveringPopupRef.current && popupModeRef.current === "cluster-hover") {
                popupRef.current.close();
                popupModeRef.current = null;
              }
            }, 180);
          }
        });

      // Show a readable count for clusters (only when multiple projects are present)
      const clusterCountLayer = new atlas.layer.SymbolLayer(dataSource, null, {
        filter: [
          "all",
          ["has", "point_count"],
          [">", ["get", "point_count"], 1], // only when more than one
        ],
        textOptions: {
          textField: ["get", "point_count_abbreviated"],
          // Position the count above the bubble; tweak Y offset as needed
          offset: [0, -1],
          color: "#ffffff",
          size: 14,
          haloColor: "#000000",
          haloWidth: 3,
          allowOverlap: true,
        },
      });
      map.layers.add(clusterCountLayer);

      // Also attach handlers to the count layer so hovering/clicking the number shows the list
      map.events.add("mouseenter", clusterCountLayer, (e) => {
          console.debug("clusterCountLayer mouseenter", { shapes: e?.shapes });
          map.getCanvasContainer().style.cursor = "pointer";
          if (hoverCloseTimerRef.current) {
            clearTimeout(hoverCloseTimerRef.current);
            hoverCloseTimerRef.current = null;
          }
          if (!e.shapes || e.shapes.length === 0) return;
          const shape = e.shapes[0];
          const props = getShapeProperties(shape);
          const coordinates = getShapeCoordinates(shape);
          if (!coordinates) return;
          const clusterId = props.cluster_id ?? props.clusterId;
          if (clusterId == null) return;

          if (popupModeRef.current === "cluster-hover" && popupRef.current?.getOptions()?.position?.toString() === coordinates.toString()) {
            return;
          }

          getClusterLeaves(clusterId, 50).then((features) => {
            console.debug("getClusterLeaves success (count layer)", { clusterId, count: features?.length });
            if (!features || features.length === 0) return;
            if (!popupRef.current) return;
            popupModeRef.current = "cluster-hover";
            popupRef.current.setOptions({
              closeButton: false,
              content: buildClusterPopupContent(features, coordinates),
              position: coordinates,
              pixelOffset: [0, 0],
            });
            openAndSanitize(popupRef.current);
          })
          .catch((err) => {
            console.error("getClusterLeaves failed (count layer)", err);
            if (!popupRef.current) return;
            popupModeRef.current = "cluster-hover";
            const fallback = document.createElement("div");
            fallback.textContent = "Projects available — click cluster to view";
            fallback.style.padding = "8px";
            popupRef.current.setOptions({ closeButton: false, content: fallback, position: coordinates, pixelOffset: [0, 0] });
            openAndSanitize(popupRef.current);
          });
      });

      map.events.add("mouseleave", clusterCountLayer, () => {
        map.getCanvasContainer().style.cursor = "grab";
        if (popupModeRef.current === "cluster-hover") {
          if (hoverCloseTimerRef.current) {
            clearTimeout(hoverCloseTimerRef.current);
            hoverCloseTimerRef.current = null;
          }
          hoverCloseTimerRef.current = setTimeout(() => {
            if (!isHoveringPopupRef.current && popupModeRef.current === "cluster-hover") {
              if (popupRef.current) popupRef.current.close();
              popupModeRef.current = null;
            }
          }, 250);
        }
      });

      map.events.add("click", clusterCountLayer, (e) => {
        if (!e.shapes || e.shapes.length === 0) return;
        const shape = e.shapes[0];
        const props = getShapeProperties(shape);
        const coordinates = getShapeCoordinates(shape);
        if (!coordinates) return;
        const clusterId = props.cluster_id ?? props.clusterId;
        if (clusterId == null) return;

        suppressNextDocCloseRef.current = true;
        setTimeout(() => (suppressNextDocCloseRef.current = false), 0);

        getClusterLeaves(clusterId, 500).then((features) => {
          if (!features || features.length === 0) return;
          if (!popupRef.current) return;
          popupModeRef.current = "cluster-click";
          popupRef.current.setOptions({
            closeButton: true,
            content: buildClusterPopupContent(features, coordinates),
            position: coordinates,
            pixelOffset: [0, -10],
          });
          openAndSanitize(popupRef.current);
        });
      });

      const symbolLayer = new atlas.layer.SymbolLayer(dataSource, null, {
        filter: ["!", ["has", "point_count"]],
        iconOptions: {
          image: colorScheme === "dark" ? "marker-darkblue" : "marker-blue",
          anchor: "bottom",
          allowOverlap: true,
          ignorePlacement: true,
          size: 1,
        },
      });
      map.layers.add(symbolLayer);

      const buildPopupContent = (properties, { variant }) => {
        const isDark = colorScheme === "dark";
        const bg = isDark ? "#1f2729" : "white";
        const titleColor = isDark ? "#ffffff" : "#2e3032";
        const subColor = isDark ? "#d5d7e0" : "#666";
        const metaColor = isDark ? "#a0a3b1" : "#888";
        const padding = variant === "hover" ? "8px 10px" : "10px";
        const shadow =
          variant === "hover"
            ? "0 2px 8px rgba(0,0,0,0.20)"
            : "0 2px 8px rgba(0,0,0,0.15)";

        const root = document.createElement("div");
        root.style.padding = padding;
        root.style.fontFamily = "sans-serif";
        root.style.background = bg;
        root.style.borderRadius = "4px";
        root.style.boxShadow = shadow;

        // Keep hover tooltip open while interacting with the popup content.
        root.addEventListener("mouseenter", () => {
          isHoveringPopupRef.current = true;
          if (hoverCloseTimerRef.current) {
            clearTimeout(hoverCloseTimerRef.current);
            hoverCloseTimerRef.current = null;
          }
        });
        root.addEventListener("mouseleave", () => {
          isHoveringPopupRef.current = false;
          if (popupModeRef.current === "hover") {
            popup.close();
            popupModeRef.current = null;
          }
        });

        // If this feature represents multiple projects at same coords,
        // render a scrollable list like the cluster popup.
        if (Array.isArray(properties.projects) && properties.projects.length > 1) {
          const title = document.createElement("div");
          title.textContent = `Projects (${properties.projects.length})`;
          title.style.fontWeight = "700";
          title.style.fontSize = "14px";
          title.style.marginBottom = "6px";
          root.appendChild(title);

          properties.projects.forEach((p) => {
            const item = document.createElement("div");
            item.textContent = p.projectName || "Unknown Project";
            item.style.fontSize = "13px";
            item.style.color = "#0087CD";
            item.style.cursor = "pointer";
            item.style.padding = "4px 0";
            item.style.textDecoration = "underline";
            item.setAttribute("role", "button");
            item.setAttribute("tabindex", "0");
            item.addEventListener("click", (e) => {
              e.preventDefault();
              e.stopPropagation();
              if (typeof onProjectOpen === "function") {
                onProjectOpen(p.projectId, p.designId, p.designVersionId);
              }
              if (popupRef.current) popupRef.current.close();
              popupModeRef.current = null;
            });
            item.addEventListener("keydown", (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                if (typeof onProjectOpen === "function") {
                  onProjectOpen(p.projectId, p.designId, p.designVersionId);
                }
                if (popupRef.current) popupRef.current.close();
                popupModeRef.current = null;
              }
            });
            root.appendChild(item);
          });
        } else {
          const title = document.createElement("div");
          title.textContent = properties.projectName || "Unknown Project";
          title.style.fontSize = "14px";
          title.style.fontWeight = "700";
          title.style.display = "block";
          title.style.marginBottom = "4px";
          title.style.color = titleColor;
          title.style.cursor = "pointer";
          title.style.textDecoration = "underline";
          title.setAttribute("role", "button");
          title.setAttribute("tabindex", "0");
          title.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (typeof onProjectOpen === "function") {
              onProjectOpen(properties.projectId, properties.designId, properties.designVersionId);
            }
          });
          title.addEventListener("keydown", (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              e.stopPropagation();
              if (typeof onProjectOpen === "function") {
                onProjectOpen(properties.projectId, properties.designId, properties.designVersionId);
              }
            }
          });
          root.appendChild(title);
        }

        if (properties.customerName) {
          const customer = document.createElement("div");
          customer.textContent = properties.customerName;
          customer.style.fontSize = "12px";
          customer.style.color = subColor;
          customer.style.display = "block";
          customer.style.marginBottom = "2px";
          root.appendChild(customer);
        }

        if (properties.city || properties.country) {
          const meta = document.createElement("div");
          const city = properties.city || "";
          const country = properties.country || "";
          meta.textContent = `${city}${city && country ? ", " : ""}${country}`;
          meta.style.fontSize = "11px";
          meta.style.color = metaColor;
          root.appendChild(meta);
        }

        return root;
      };

      const openPopup = (variant, shape) => {
        if (!shape) return;
        const properties = getShapeProperties(shape);
        const coordinates = getShapeCoordinates(shape);
        if (!coordinates) return;

        // If a delayed close is pending, cancel it.
        if (hoverCloseTimerRef.current) {
          clearTimeout(hoverCloseTimerRef.current);
          hoverCloseTimerRef.current = null;
        }

        popupModeRef.current = variant;
        popup.setOptions({
          closeButton: variant === "click",
          content: buildPopupContent(properties, { variant }),
          position: coordinates,
          pixelOffset: [0, -30],
        });
        openAndSanitize(popup);
      };

      map.events.add("click", symbolLayer, (e) => {
        if (e.shapes && e.shapes.length > 0) {
          // Prevent the document-level outside-click handler from immediately closing
          // the popup that we're about to open.
          suppressNextDocCloseRef.current = true;
          setTimeout(() => {
            suppressNextDocCloseRef.current = false;
          }, 0);
          openPopup("click", e.shapes[0]);
        }
      });

      // Clicking a cluster opens a popup listing the projects in that cluster
      map.events.add("click", clusterBubbleLayer, (e) => {
        if (!e.shapes || e.shapes.length === 0) return;
        const shape = e.shapes[0];
        const props = getShapeProperties(shape);
        const coordinates = getShapeCoordinates(shape);
        if (!coordinates) return;

        const clusterId = props.cluster_id ?? props.clusterId;
        if (clusterId == null) return;

        // Prevent immediate document click-close
        suppressNextDocCloseRef.current = true;
        setTimeout(() => (suppressNextDocCloseRef.current = false), 0);

        getClusterLeaves(clusterId, 500).then((features) => {
          if (!features || features.length === 0) return;
          if (!popupRef.current) return;
          popupModeRef.current = "cluster-click";
          popupRef.current.setOptions({
            closeButton: true,
            content: buildClusterPopupContent(features, coordinates),
            position: coordinates,
            pixelOffset: [0, -10],
          });
          openAndSanitize(popupRef.current);
        });
      });

      // Close a click-opened popup or cluster-click popup when clicking away.
      map.events.add("click", (e) => {
        if (popupModeRef.current !== "click" && popupModeRef.current !== "cluster-click") return;

        // If click hits a shape (e.g., a marker), let the layer handler manage it.
        if (e?.shapes && e.shapes.length > 0) return;

        const target = e?.originalEvent?.target;
        console.debug("map click handler: checking outside click", { mode: popupModeRef.current, target });
        if (target && typeof target.closest === "function") {
          if (target.closest(".atlas-popup") || target.closest(".atlas-popup-container")) {
            console.debug("map click inside popup — keeping open");
            return;
          }
        }

        if (popupRef.current) popupRef.current.close();
        popupModeRef.current = null;
      });

      // Also close when clicking anywhere outside the popup (including map controls
      // or outside the map entirely). Handle both `click` and `cluster-click` modes.
      const onDocumentPointerDown = (ev) => {
        if (popupModeRef.current !== "click" && popupModeRef.current !== "cluster-click") return;
        if (suppressNextDocCloseRef.current) {
          console.debug("document pointerdown suppressed by flag");
          return;
        }

        const target = ev?.target;
        if (target && typeof target.closest === "function") {
          if (target.closest(".atlas-popup") || target.closest(".atlas-popup-container")) {
            console.debug("document pointerdown inside popup — keeping open");
            return;
          }
        }

        console.debug("document pointerdown closing popup", { mode: popupModeRef.current, target });
        if (popupRef.current) popupRef.current.close();
        popupModeRef.current = null;
      };

      documentPointerHandlerRef.current = onDocumentPointerDown;
      document.addEventListener("pointerdown", onDocumentPointerDown, true);

      map.events.add("mouseenter", symbolLayer, (e) => {
        map.getCanvasContainer().style.cursor = "pointer";
        if (popupModeRef.current === "click") return;
        isHoveringPopupRef.current = false;
        if (e.shapes && e.shapes.length > 0) {
          openPopup("hover", e.shapes[0]);
        }
      });

      map.events.add("mousemove", symbolLayer, (e) => {
        if (popupModeRef.current === "click") return;
        isHoveringPopupRef.current = false;
        if (e.shapes && e.shapes.length > 0) {
          openPopup("hover", e.shapes[0]);
        }
      });

      map.events.add("mouseleave", symbolLayer, () => {
        map.getCanvasContainer().style.cursor = "grab";
        if (popupModeRef.current === "hover") {
          if (hoverCloseTimerRef.current) {
            clearTimeout(hoverCloseTimerRef.current);
            hoverCloseTimerRef.current = null;
          }
          // Delay closing to allow moving pointer from marker to popup.
          hoverCloseTimerRef.current = setTimeout(() => {
            if (!isHoveringPopupRef.current && popupModeRef.current === "hover") {
              popup.close();
              popupModeRef.current = null;
            }
          }, 150);
        }
      });

      // Show pointer cursor on clusters.
      map.events.add("mouseenter", clusterBubbleLayer, () => {
        map.getCanvasContainer().style.cursor = "pointer";
      });
      map.events.add("mouseleave", clusterBubbleLayer, () => {
        map.getCanvasContainer().style.cursor = "grab";
      });

      if (validProjects.length && mapContainerRef.current) {
      addMarkers(dataSource, map);
      }
    });

    return () => {
      disposed = true;
      if (hoverCloseTimerRef.current) {
        clearTimeout(hoverCloseTimerRef.current);
        hoverCloseTimerRef.current = null;
      }
      if (documentPointerHandlerRef.current) {
        document.removeEventListener(
          "pointerdown",
          documentPointerHandlerRef.current,
          true
        );
        documentPointerHandlerRef.current = null;
      }
      if (popupRef.current) { try { popupRef.current.close(); } catch (e) {} }
      if (mapRef.current) {
        try { mapRef.current.dispose(); } catch (e) {}
        mapRef.current = null;
      }
      controlsInitializedRef.current = false;
      setMapInitialized(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validProjects.length, colorScheme]);

  // When computed height changes, tell the map to resize so tiles render correctly
  useEffect(() => {
    if (!mapInitialized) return;
    try {
      applyMapZoomCompensation();
      if (mapRef.current && typeof mapRef.current.resize === "function") {
        mapRef.current.resize();
      }
    } catch (e) {
      // ignore
    }
  }, [computedHeight, mapInitialized]);

  useEffect(() => {
    if (mapInitialized && mapRef.current && dataSourceRef.current && mapContainerRef.current) {
      addMarkers(dataSourceRef.current, mapRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validProjects, mapInitialized]);

  useEffect(() => {
    if (!mapInitialized) return;
    if (!resetCounter) return;
    resetMapView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetCounter, mapInitialized]);

  if (!validProjects.length) {
    return (
      <div
        style={{
          width: "100%",
          height: computedHeight,
          minHeight: "500px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: colorScheme === "dark" ? "#ccc" : "#666",
          fontFamily: "sans-serif",
          fontSize: "16px",
        }}
      >
        No projects with valid location data available
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      {/* <div style={{ position: 'absolute', left: 12, top: 12, zIndex: 5, display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Search project or customer"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
          style={{ padding: '6px 8px', borderRadius: 4, border: '1px solid rgba(0,0,0,0.12)' }}
        />
        <button type="button" aria-label="project-map-search" onClick={() => handleSearch()} style={{ padding: '6px 8px', borderRadius: 4, border: '1px solid rgba(0,0,0,0.12)', cursor: 'pointer' }}>🔍</button>
      </div> */}
      <button
        type="button"
        onClick={resetMapView}
        style={{
          position: "absolute",
          top: 148,
          right: 10,
          zIndex: 10,
          height: 28,
          padding: "0 10px",
          border: "1px solid rgba(0,0,0,0.18)",
          borderRadius: 4,
          background: colorScheme === "dark" ? "#1f2729" : "#fff",
          color: colorScheme === "dark" ? "#fff" : "#222",
          fontSize: 12,
          fontWeight: 600,
          cursor: "pointer",
        }}
        aria-label="Reset map view"
      >
        Reset
      </button>

      <div
        ref={mapContainerRef}
        id={containerId.current}
        style={{
          width: "100%",
          height: computedHeight,
          minHeight: "535px",
        }}
      />
    </div>
  );
};

export default ProjectMapView;
